// [S-061c] Brief composer — form that submits to POST /briefs and
// navigates to /briefs/:id on success. The compose chain (template
// anchor + precedents search) runs server-side; this screen is just
// the input form + status surface.
//
// Wireframe: MISSING — Master Rule 1.10 build-now-retrofit-later.
// Token usage stays on `--cs-source-cert{,-bg,-text}` for L1 affordances
// (page eyebrow) and `--cs-source-stud{,-bg,-text}` for the L2 chip.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useComposeBrief, type BriefInput } from "../data/briefsQuery";
import { Alert, Button, PageHead, Select } from "../components";
import { TALENT_TIERS } from "../lib/talentTier";

const DEAL_TYPES: ReadonlyArray<{ value: BriefInput["dealType"]; label: string }> = [
  { value: "feature_film", label: "Feature film" },
  { value: "tv_series", label: "TV series" },
  { value: "streaming", label: "Streaming" },
  { value: "documentary", label: "Documentary" },
  { value: "commercial", label: "Commercial" },
  { value: "short_form", label: "Short form" },
];

const GUILDS: ReadonlyArray<{ value: BriefInput["guild"]; label: string }> = [
  { value: "SAG", label: "SAG-AFTRA" },
  { value: "DGA", label: "DGA" },
  { value: "WGA", label: "WGA" },
  { value: "IATSE", label: "IATSE" },
  { value: "AFM", label: "AFM" },
  { value: "PGA", label: "PGA" },
];

const TIERS = TALENT_TIERS;

export function BriefCompose(): JSX.Element {
  const navigate = useNavigate();
  const compose = useComposeBrief();
  const [input, setInput] = useState<BriefInput>({
    dealType: "feature_film",
    guild: "SAG",
    talentTier: "professional",
    opposingCounselPosture: "",
    keyTermsInFlight: "",
  });

  const canSubmit =
    input.opposingCounselPosture.trim().length > 0 &&
    input.keyTermsInFlight.trim().length > 0 &&
    !compose.isPending;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    compose.mutate(input, {
      onSuccess: (brief) => navigate(`/briefs/${brief.id}`),
    });
  };

  return (
    <>
      <PageHead
        eyebrow={
          <>
            <span className="src-dot cert" aria-hidden="true" />
            Pre-negotiation brief
          </>
        }
        title={
          <>
            Walk in <em>knowing</em> the room.
          </>
        }
        sub="A composed brief blends the certified templates (L1) with your studio's prior deals (L2) — kept in separate sections, never merged, so the source of every term is visible."
      />
      <form className="brf__form" onSubmit={handleSubmit}>
        <div className="brf__row">
          <Field
            label="Deal type"
            value={input.dealType}
            onChange={(v) => setInput({ ...input, dealType: v as BriefInput["dealType"] })}
            options={DEAL_TYPES}
          />
          <Field
            label="Guild"
            value={input.guild}
            onChange={(v) => setInput({ ...input, guild: v as BriefInput["guild"] })}
            options={GUILDS}
          />
          <Field
            label="Talent tier"
            value={input.talentTier}
            onChange={(v) => setInput({ ...input, talentTier: v as BriefInput["talentTier"] })}
            options={TIERS}
          />
        </div>

        <label className="brf__label">
          Opposing counsel posture
          <textarea
            id="brief-opposing-posture"
            className="brf__textarea"
            rows={2}
            value={input.opposingCounselPosture}
            onChange={(e) =>
              setInput({ ...input, opposingCounselPosture: e.target.value })
            }
            placeholder="e.g. WME pushing for uncapped backend; favored-nations clause."
          />
        </label>

        <label className="brf__label">
          Key terms in flight
          <textarea
            id="brief-key-terms"
            className="brf__textarea"
            rows={3}
            value={input.keyTermsInFlight}
            onChange={(e) =>
              setInput({ ...input, keyTermsInFlight: e.target.value })
            }
            placeholder="e.g. backend cap, term length, governing law, weekly comp."
          />
        </label>

        <Button
          type="submit"
          variant="bare"
          className="brf__submit"
          disabled={!canSubmit}
          aria-label="Compose brief"
        >
          {compose.isPending ? "Composing…" : "Compose brief"}
        </Button>

        {compose.isError && (
          <Alert label="Couldn't compose the brief" tone="error">
            {compose.error.message}
          </Alert>
        )}
      </form>
    </>
  );
}

function Field({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  onChange: (value: string) => void;
}): JSX.Element {
  return (
    <label className="brf__field">
      <span>{label}</span>
      <Select
        label={label}
        value={value}
        options={options}
        clearable={false}
        onValueChange={(v) => v !== undefined && onChange(v)}
        triggerClassName="brf__select"
      />
    </label>
  );
}
